import { useCallback, useState } from "react";
import { useMountedState } from "react-use";

/**
 * Wraps an async function and exposes its execution state.
 *
 * Intended for cases where an async operation is triggered manually
 * and the UI needs to know whether it is currently running.
 *
 * @param fn Async function to wrap
 *
 * @returns Object containing:
 * - `callback`: wrapped async function
 * - `isLoading`: execution state, `true` only while the async operation is running
 * - `error`: last error or null
 */
// oxlint-disable-next-line @typescript-eslint/no-explicit-any
export function useAsyncCallback<TArgs extends any[], TResult>(
  fn: (...args: TArgs) => Promise<TResult>,
) {
  const isMounted = useMountedState();
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<unknown>(null);

  const callback = useCallback(
    async (...args: TArgs) => {
      if (isMounted()) {
        setIsLoading(true);
        setError(null);
      }
      try {
        return await fn(...args);
      } catch (e) {
        if (isMounted()) setError(e);
        throw e;
      } finally {
        if (isMounted()) setIsLoading(false);
      }
    },
    [fn, isMounted],
  );

  return { callback, isLoading, error };
}
